import { Link } from "react-router-dom";
import { ArrowRight, Scale, Music, Disc3, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const COPYRIGHTS = [
  {
    name: "Musical Composition",
    code: "PA",
    icon: Music,
    description:
      "The underlying song — melody, harmony, lyrics, structure. Owned initially by the songwriter(s) and typically administered by a music publisher. Generates performance, mechanical, sync, and print royalties.",
    examples: [
      "Lyrics + melody written by a songwriter",
      "Instrumental composition by a film composer",
      "Co-written hooks, verses, choruses",
    ],
    registration: "U.S. Form PA (Performing Arts) at copyright.gov",
  },
  {
    name: "Sound Recording (Master)",
    code: "SR",
    icon: Disc3,
    description:
      "The specific recorded performance of a composition — the master. Owned initially by the recording artist (or assigned to a label). Generates streaming, download, sync (master side), and neighbouring-rights royalties.",
    examples: [
      "A studio master recording of a song",
      "A live concert recording",
      "A remix master",
    ],
    registration: "U.S. Form SR (Sound Recordings) at copyright.gov",
  },
];

const ROYALTIES = [
  {
    n: "01",
    name: "Performance Royalties",
    desc: "Triggered when a composition is publicly performed — on radio, TV, in streaming services, venues, restaurants, and broadcasts. Collected by PROs (ASCAP, BMI, SESAC, GMR; SACEM, GEMA, PRS, etc. abroad) and paid to songwriters and publishers.",
    paidTo: "Songwriter + Publisher",
    collectedBy: "PROs",
  },
  {
    n: "02",
    name: "Mechanical Royalties",
    desc: "Reproduction royalty paid each time a composition is reproduced — physical sales (CDs, vinyl), permanent downloads, and on-demand interactive streams. In the U.S., administered by The MLC (post-MMA); Harry Fox Agency handles many catalogs.",
    paidTo: "Songwriter + Publisher",
    collectedBy: "MLC / HFA / MROs",
  },
  {
    n: "03",
    name: "Synchronization Royalties",
    desc: "Per-use fees negotiated when a composition is paired with visual media — film, TV, ads, video games, YouTube, TikTok. There is no statutory rate; every sync is a negotiated license.",
    paidTo: "Songwriter + Publisher",
    collectedBy: "Direct license · Publisher",
  },
  {
    n: "04",
    name: "Neighbouring Rights Royalties",
    desc: "Paid to performers and master-recording owners for broadcasts and public performances of recordings. Most countries pay both songwriter side AND performer/master side; in the U.S. terrestrial radio is an exception (no master pay).",
    paidTo: "Performer + Master owner",
    collectedBy: "PPL, Re:Sound, etc.",
  },
  {
    n: "05",
    name: "Digital Performance Royalties",
    desc: "U.S.-specific statutory royalty for non-interactive digital streams (Pandora radio, SiriusXM, internet radio). Collected by SoundExchange and distributed: 50% master owner, 45% featured artist, 5% non-featured AFM/SAG-AFTRA fund.",
    paidTo: "Master owner + Featured artist + Non-featured musicians",
    collectedBy: "SoundExchange",
  },
  {
    n: "06",
    name: "Print Royalties",
    desc: "Per-copy royalty paid on the sale of printed sheet music, folios, hymnals, choral arrangements, and digital sheet music. Typically 10-15% of retail to the publisher who then splits with the songwriter.",
    paidTo: "Songwriter + Publisher",
    collectedBy: "Print publishers (Hal Leonard, Alfred, Wise)",
  },
];

export default function CopyrightLawsPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#0A1128] pt-28 pb-20" data-testid="copyright-laws-page">
      <div className="max-w-7xl mx-auto px-6">
        <nav className="text-xs text-slate-500 flex items-center gap-2 mb-6">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span>/</span>
          <Link to="/rights-library" className="hover:text-white transition-colors">Rights Library</Link>
          <span>/</span>
          <span className="text-[#B8D0EE]">Music Copyright Laws</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/5 mb-6">
              <Scale className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold" style={{ color: "#D4AF37" }}>
                Foundations · Music Copyright Laws
              </span>
            </div>
            <h1 className="font-display text-4xl lg:text-7xl tracking-tight leading-[0.98]" data-testid="laws-title">
              The two copyrights. <br />
              <span className="text-[#7DA3D9]">The six royalty streams.</span>
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-slate-300 leading-relaxed text-lg">
              Every dollar a music creator earns flows through one of two distinct copyrights —
              and lands as one of six royalty types. Master this map and every contract,
              statement, and split will start making sense.
            </p>
          </div>
        </div>

        {/* TWO COPYRIGHTS */}
        <section className="mb-20" data-testid="two-copyrights">
          <div className="overline mb-3">Part One</div>
          <h2 className="font-display text-3xl lg:text-5xl mb-3 leading-tight">
            The two distinctive music copyrights
          </h2>
          <p className="text-slate-400 max-w-3xl mb-10">
            U.S. copyright law (and most national laws under the Berne Convention) recognizes
            two separate copyrights in every released song. They are owned, licensed, and paid out
            independently — even though they describe the same piece of music.
          </p>

          <div className="grid lg:grid-cols-2 gap-6">
            {COPYRIGHTS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.name} className="glass-card p-8" data-testid={`copyright-${c.code.toLowerCase()}`}>
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-14 h-14 rounded-md bg-[#7DA3D9]/10 border border-[#7DA3D9]/30 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-[#7DA3D9]" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                        Form {c.code}
                      </div>
                      <h3 className="font-display text-2xl lg:text-3xl mt-1">{c.name}</h3>
                    </div>
                  </div>
                  {user ? (
                    <>
                      <p className="text-slate-300 leading-relaxed mb-6">{c.description}</p>
                      <div className="border-t border-white/5 pt-5 space-y-2">
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Examples</div>
                        <ul className="space-y-1.5 text-sm text-slate-300">
                          {c.examples.map((e, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-[#7DA3D9] mt-0.5">→</span>
                              <span>{e}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="mt-5 text-xs text-slate-400">
                        <span className="text-slate-500">Registration: </span>
                        {c.registration}
                      </div>
                    </>
                  ) : (
                    <div data-testid={`copyright-${c.code.toLowerCase()}-locked`}>
                      <p className="text-slate-300 leading-relaxed mb-6 blur-sm select-none pointer-events-none">
                        {c.description}
                      </p>
                      <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#7DA3D9] hover:text-[#B8D0EE]"
                      >
                        <Lock className="w-3.5 h-3.5" /> Sign in to view definition
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* SIX ROYALTIES */}
        <section className="mb-20" data-testid="six-royalties">
          <div className="overline mb-3">Part Two</div>
          <h2 className="font-display text-3xl lg:text-5xl mb-3 leading-tight">
            The six primary royalty streams
          </h2>
          <p className="text-slate-400 max-w-3xl mb-10">
            Every check a music creator receives belongs to one of these six streams. Know
            which ones apply to you, who collects them, and where your money is supposed to land.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-md overflow-hidden border border-white/5">
            {ROYALTIES.map((r) => (
              <div key={r.n} className="p-8 bg-[#0A1128] hover:bg-white/[0.02] transition-colors" data-testid={`royalty-${r.n}`}>
                <div className="font-display text-4xl text-[#7DA3D9]/50 mb-3">{r.n}</div>
                <h3 className="font-display text-xl mb-3 text-white">{r.name}</h3>
                {user ? (
                  <>
                    <p className="text-sm text-slate-300 leading-relaxed mb-5">{r.desc}</p>
                    <div className="border-t border-white/5 pt-4 space-y-2 text-xs">
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500 uppercase tracking-wider">Paid to</span>
                        <span className="text-slate-300 text-right">{r.paidTo}</span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500 uppercase tracking-wider">Collected by</span>
                        <span className="text-[#B8D0EE] text-right">{r.collectedBy}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div data-testid={`royalty-${r.n}-locked`}>
                    <p className="text-sm text-slate-300 leading-relaxed mb-5 blur-sm select-none pointer-events-none">
                      {r.desc}
                    </p>
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#7DA3D9] hover:text-[#B8D0EE]"
                    >
                      <Lock className="w-3.5 h-3.5" /> Sign in to view
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA — only shown to non-paid users */}
        {!user?.has_complete_access && (
          <section className="glass-card p-10 lg:p-14 text-center" data-testid="laws-cta">
            <div className="overline" style={{ color: "#D4AF37" }}>Limited time · only $6.95 to join</div>
            <h2 className="font-display text-3xl lg:text-5xl mt-4 leading-tight">
              Ready to collect every royalty owed to you?
            </h2>
            <p className="text-slate-300 mt-5 max-w-2xl mx-auto">
              Unlock the full OSRR registry — 200+ agencies across 160 countries — for a
              limited-time $6.95 lifetime membership.
            </p>
            <div className="mt-10">
              <Link to="/upgrade" className="btn-gold inline-flex items-center gap-2" data-testid="laws-cta-unlock">
                Unlock for $6.95 <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
